/**
 * Speed-Shield Drag
 *
 * Passive shield drain that scales with current velocity.
 * Faster ships bleed shield while moving fast; slow tanks barely lose anything.
 * Dune-inspired "shields hate speed" mechanic.
 *
 * Applied server-side in updateRegeneration() before regen.
 */

export type EngineDragType = 'proton' | 'ion' | 'plasma';
export type ShieldDragType = 'deflector' | 'absorber' | 'regenerator';

// Base coefficient: 2% of current speed drained per second from shield.
// Tune this single value to make drain feel stronger/weaker globally.
export const SHIELD_DRAG_BASE = 0.02;

// Velocity threshold (fraction of maxSpeed) below which drain is zero.
export const SHIELD_DRAG_SPEED_THRESHOLD = 0.3;

// Per-engine multiplier: how "clean" the engine is for the shield.
export const ENGINE_DRAG_MULTIPLIER: Record<EngineDragType, number> = {
  proton: 1.0,   // baseline starter engine
  ion: 0.85,     // clean, efficient
  plasma: 1.4,   // raw speed, interferes with shield emitters
};

// Per-shield multiplier: how well the shield handles drag.
export const SHIELD_DRAG_RESISTANCE: Record<ShieldDragType, number> = {
  deflector: 0.7,    // capacity-focused, tanks drag well
  absorber: 1.0,     // baseline
  regenerator: 1.3,  // thin shield, drag hits harder
};

/**
 * Compute per-second shield drain from velocity.
 * Returns 0 if below threshold, if no engine, or if no shield equipped.
 */
// Combat-overhaul: shield drag was eliminated by spec. Shields are now native to
// the ship and don't have HP — there's nothing to drain. We keep the export
// signature so existing callers compile, but it always returns 0 (no-op).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function computeShieldDrag(_params: {
  currentSpeed: number;
  maxSpeed: number;
  engineType: string;
  shieldType: string;
}): number {
  return 0;
}
